
<div class="ShiftsTable">
    <BaseDashboardTable
        ref="dashboardTable"
        :columns="columns"
        :items="formattedShifts"
        :sort="sortState"
        :empty-message="'Keine Schichten vorhanden'"
        :pagination="paginationConfig"
        :container-height="containerHeight"
        :use-item-virtualization="true"
        :isClickable="true"
        :show-legend="true"
        :legend-items="legendItems"
        :searchable-columns="[
            'driver',
            'sequenceNumber',
            'licenseNumber',
            'startAt',
            'endAt',
            'finalTotalAmount',
            'tips',
            'cashAmount',
        ]"
        :search-placeholder="'Schichten durchsuchen...'"
        page-id="shifts-dashboard"
        table-id="shifts-table"
        :show-column-toggler="true"
        @sort-changed="handleSortChanged"
        @row-click="handleShiftClick"
        @toggle-expand="handleToggleExpand"
        @row-hover="(args) => $emit('onRowHover', args)"
        @row-leave="(args) => $emit('onRowLeave', args)"
        @expand-on-match="handleExpandOnMatch"
        @search-cleared="handleSearchCleared"
        @page-changed="handlePageChanged"
    >
        <template #title>
            <h3 id="shifts-table-header">Schichten</h3>
        </template>
        <template #column-tips="{ item }">
            {{ item.tips }}
        </template>

        <template #column-gesamtumsatz="{ item }">
            <div class="amount-cell">
                <span
                    :class="{
                        'warning-text': item.hasWarning,
                        'empty-trip-bg': item.isEmptyTrip,
                    }"
                >
                    {{ item.gesamtumsatz }}
                </span>
            </div>
        </template>
        <template #column-driver="{ item }">
            <DriverPopover
                v-if="!item._isChild && item.driver !== 'Unangemeldet'"
                :driver-name="item.driver"
                :driver-number="item.driverNumber || 'unknown'"
                :driver-id="item.driverId || null"
                class="tutorial-driver-name"
                @filter="handleDriverFilter"
                @changeDriver="(event) => handleChangeDriver(event, item)"
                @click.stop
            />
            <span v-else-if="!item._isChild" class="unregistered-driver">
                {{ item.driver }}
            </span>
        </template>

        <template #column-licenseNumber="{ item }">
            <LicenseNumberPopover
                v-if="!item._isChild"
                :license-number="item.licenseNumber"
                :car-id="item.originalShift?.car?.id || item.originalShift?.car?.uuid"
                :car-uuid="item.originalShift?.car?.uuid"
                :concession="item.concession"
                @filter="handleCarFilter"
                @changeCar="handleChangeCar"
                @click.stop
            />
        </template>

        <template #column-concession="{ item }">
            <ConcessionPopover
                v-if="item.concession && !item._isChild"
                :concession="item.concession"
                :car-id="item.licenseNumber"
                :car-uuid="item.car?.uuid"
                @filter="handleConcessionFilter"
            />
        </template>

        <template #column-finalTotalAmount="{ item }">
            <div class="amount-cell">
                <span
                    :class="{
                        'warning-text': item.hasWarning,
                        'empty-trip-bg': item.isEmptyTrip,
                        'flatrate-trip-bg': item.isFlatRate,
                    }"
                >
                    {{ item.finalTotalAmount }}
                </span>
            </div>
        </template>

        <template #column-subtractedTotalAmount="{ item }">
            <span
                v-if="item.subtractedTotalAmount !== '--:--'"
                :class="{
                    'warning-text': item._subtractedTotalAmount < 0,
                    'positive-text': item._subtractedTotalAmount > 0,
                    'tutorial-correction-cell': true,
                }"
            >
                {{ item.subtractedTotalAmount }}
            </span>
            <span v-else>{{ item.subtractedTotalAmount }}</span>
        </template>

        <template #column-cashAmount="{ item }">
            <div class="amount-cell">
                <span
                    :class="{
                        'warning-text': item.hasWarning,
                        'empty-trip-bg': item.isEmptyTrip,
                    }"
                >
                    {{ item.cashAmount }}
                </span>
            </div>
        </template>

        <template #column-paymentMethod_uber="{ item }">
            <div class="amount-cell">
                <span>{{ item.paymentMethod_uber || '--:--' }}</span>
            </div>
        </template>

        <template #column-paymentMethod_bolt="{ item }">
            <div class="amount-cell">
                <span>{{ item.paymentMethod_bolt || '--:--' }}</span>
            </div>
        </template>

        <template #column-paymentMethod_bliq="{ item }">
            <div class="amount-cell">
                <span>{{ item.paymentMethod_bliq || '--:--' }}</span>
            </div>
        </template>

        <template #column-paymentMethod_freenow="{ item }">
            <div class="amount-cell">
                <span>{{ item.paymentMethod_freenow || '--:--' }}</span>
            </div>
        </template>

        <template #column-paymentMethod_card="{ item }">
            <div class="amount-cell">
                <span>{{ item.paymentMethod_card || '--:--' }}</span>
            </div>
        </template>

        <template #column-paymentMethod_coupon="{ item }">
            <div class="amount-cell">
                <span>{{ item.paymentMethod_coupon || '--:--' }}</span>
            </div>
        </template>

        <template #column-paymentMethod_taxieupayment="{ item }">
            <div class="amount-cell">
                <span>{{ item.paymentMethod_taxieupayment || '--:--' }}</span>
            </div>
        </template>

        <template #column-paymentMethod_taxideutschland="{ item }">
            <div class="amount-cell">
                <span>{{ item.paymentMethod_taxideutschland || '--:--' }}</span>
            </div>
        </template>

        <template #column-paymentMethod_invoice="{ item }">
            <div class="amount-cell">
                <span>{{ item.paymentMethod_invoice || '--:--' }}</span>
            </div>
        </template>

        <template #column-details="{ item }">
            <Button
                v-if="(isMobile || isTablet) && !item._isChild"
                size="small"
                variant="table"
                v-tooltip="getDetailsTooltip(item)"
                @onClick="openDetailsModal(item)"
            >
                <i class="ri-information-line"></i>
            </Button>
        </template>

        <template #column-comments="{ item }">
            <span v-if="!item._isChild" @click.stop>
                <Button
                    size="small"
                    variant="table"
                    v-tooltip="'Kommentare anzeigen'"
                    @onClick="openCommentsModal(item)"
                >
                    <i class="ri-chat-3-line"></i>
                </Button>
            </span>
        </template>
    </BaseDashboardTable>

    <ShiftCommentsModal
        :show="showCommentsModal"
        :shift="selectedShiftForComments"
        @close="closeCommentsModal"
    />

    <Modal
        :show="showDetailsModal"
        :title="'Schichtendetails'"
        @onModalClose="closeDetailsModal"
    >
        <div v-if="selectedShiftForDetails" class="shift-details-modal">
            <div class="detail-row">
                <span class="label">Fahrer:</span>
                <span class="value">{{ selectedShiftForDetails.driver }}</span>
            </div>
            <div class="detail-row">
                <span class="label">Schicht:</span>
                <span class="value">{{ selectedShiftForDetails.sequenceNumber }}</span>
            </div>
            <div class="detail-row">
                <span class="label">Kennzeichen:</span>
                <span class="value">{{ selectedShiftForDetails.licenseNumber }}</span>
            </div>
            <div class="detail-row">
                <span class="label">Start:</span>
                <span class="value">{{ selectedShiftForDetails.startAt }}</span>
            </div>
            <div class="detail-row">
                <span class="label">Ende:</span>
                <span class="value">{{ selectedShiftForDetails.endAt }}</span>
            </div>
            <div class="detail-row">
                <span class="label">Zeit:</span>
                <span class="value">{{ selectedShiftForDetails.duration }}</span>
            </div>
            <div class="detail-row">
                <span class="label">Ges.KM:</span>
                <span class="value">{{ selectedShiftForDetails.totalDistance }}</span>
            </div>
            <div class="detail-row">
                <span class="label">Bes.KM:</span>
                <span class="value">{{ selectedShiftForDetails.hiredDistance }}</span>
            </div>
            <div class="detail-row">
                <span class="label">Leer KM:</span>
                <span class="value">{{ selectedShiftForDetails.forHireDistance }}</span>
            </div>
            <div class="detail-row">
                <span class="label">Zuschl.:</span>
                <span class="value">{{ selectedShiftForDetails.finalExtrasAmount }}</span>
            </div>
            <div class="detail-row">
                <span class="label">Korrektur:</span>
                <span class="value">{{ selectedShiftForDetails.subtractedTotalAmount }}</span>
            </div>
            <div class="detail-row">
                <span class="label">Trinkgeld:</span>
                <span class="value">{{ selectedShiftForDetails.tips }}</span>
            </div>
            <div class="detail-row">
                <span class="label">Bar:</span>
                <span class="value">{{ selectedShiftForDetails.cashAmount }}</span>
            </div>
            <div class="detail-row">
                <span class="label">Umsatz:</span>
                <span class="value">{{ selectedShiftForDetails.finalTotalAmount }}</span>
            </div>
            <div v-if="selectedShiftForDetails.concession" class="detail-row">
                <span class="label">Konzession:</span>
                <span class="value">{{ selectedShiftForDetails.concession }}</span>
            </div>
            <div v-if="selectedShiftForDetails.vatRate" class="detail-row">
                <span class="label">USt.:</span>
                <span class="value">{{ selectedShiftForDetails.vatRate }}</span>
            </div>
        </div>
    </Modal>
</div>
